import {
  AssignmentProposalStatus,
  AssignmentProposalTimelineEvent,
  type AssignmentProposal,
  type AssignmentProposalDecisionResult,
  type AssignmentProposalReviewer,
  type AssignmentProposalTimelineEntry,
  type SubmitAssignmentProposalResult,
  type User,
  type UserFilterInput,
} from "@models/ISchema";
import type {
  ActiveProposal,
  ActiveProposalMode,
  ProposalDecisionOutcome,
  ProposalSavedState,
  ProposalSubmitOutcome,
  ReviewerOption,
} from "@services/types/assignment-proposal/assignmentProposalTypes";

/** Both permissions a user needs to be offered as intermediate reviewer. */
export const REVIEWER_ABILITY_CODES = [
  "SchedulerEditor.can_participate_in_approval_flow",
  "SchedulerEditor.can_pre_approve_proposals",
] as const;

export const REVIEWER_PAGE_SIZE = 10;

const SAVED_STATES: readonly ProposalSavedState[] = ["draft", "rejected"];

const toSavedState = (state: string | null | undefined): ProposalSavedState | null =>
  SAVED_STATES.find(saved => saved === state) ?? null;

/**
 * Turns the mutation result into a discriminated union. An errorCode always wins: a failed send
 * can also carry the proposalId of the content the backend managed to save (a draft, or the
 * correction of a rejected proposal), and that answer must never be read as a successful send.
 */
export const normalizeSubmitResult = (
  result: SubmitAssignmentProposalResult | null | undefined,
): ProposalSubmitOutcome => {
  if (!result) return { kind: "failed" };

  if (result.errorCode) {
    const savedState = result.proposalId ? toSavedState(result.state) : null;

    return {
      kind: "rejected",
      errorCode: result.errorCode,
      unsupportedSessionId: result.unsupportedSessionId ?? null,
      savedProposalId: savedState ? (result.proposalId ?? null) : null,
      savedState,
    };
  }

  if (result.proposalId) {
    return { kind: "sent", proposalId: result.proposalId, state: result.state ?? null };
  }

  if (result.validation) return { kind: "validation", validation: result.validation };

  return { kind: "failed" };
};

/**
 * Result of an intermediate approval or a rejection. An errorCode always wins; with it, a
 * proposalId means the reviewer's adjustment was saved although the decision was not recorded.
 */
export const normalizeDecisionResult = (
  result: AssignmentProposalDecisionResult | null | undefined,
): ProposalDecisionOutcome => {
  if (!result) return { kind: "failed" };

  if (result.errorCode) {
    return {
      kind: "rejected",
      errorCode: result.errorCode,
      savedProposalId: result.proposalId ?? null,
    };
  }

  if (result.validation) return { kind: "validation", validation: result.validation };

  if (result.status || result.proposalId)
    return { kind: "recorded", status: result.status ?? null };

  return { kind: "failed" };
};

/**
 * The reason of a rejected proposal: `rejectionReason` or, while the backend returns it null, the
 * comment of the last rejection of the approval history.
 */
export const getRejectionReason = (
  rejectionReason: string | null | undefined,
  approvalTimeline: Pick<AssignmentProposalTimelineEntry, "type" | "comment">[] | null | undefined,
): string | null => {
  const lastRejection = [...(approvalTimeline ?? [])]
    .reverse()
    .find(entry => entry?.type === AssignmentProposalTimelineEvent.Rejected);

  return rejectionReason?.trim() || lastRejection?.comment?.trim() || null;
};

/** users filter: umbrella AND intermediate approval (abilities combine only through "and"). */
export const buildReviewerFilter = (
  searchTerm: string | null | undefined,
  page: number,
  size: number = REVIEWER_PAGE_SIZE,
): UserFilterInput => ({
  fields: { and: REVIEWER_ABILITY_CODES.map(code => ({ abilities: { eq: code } })) },
  pagination: { page, size, ...(searchTerm ? { searchTerm } : {}) },
});

type NamedUser = Pick<AssignmentProposalReviewer, "username" | "name" | "lastName">;

const fullNameOf = ({ name, lastName }: NamedUser): string =>
  [name, lastName].filter(Boolean).join(" ");

/** "username - name lastName", or only the username when Darwin has no name for the user. */
const reviewerLabel = (user: NamedUser): string => {
  const fullName = fullNameOf(user);

  return fullName ? `${user.username} - ${fullName}` : user.username;
};

/** "name lastName" of a user of the flow, or the username when Darwin has no name for them. */
export const userDisplayName = (user: NamedUser): string => fullNameOf(user) || user.username;

/** The value is the username the mutation needs. */
export const toReviewerOption = (
  user: Pick<User, "id" | "username" | "name" | "lastName">,
): ReviewerOption => ({ label: reviewerLabel(user), value: user.username });

/** Options of the reviewer select without the logged user, who cannot review their own proposal. */
export const withoutUser = <T extends Pick<User, "id">>(
  users: T[] | null | undefined,
  userId: string | number | null | undefined,
): T[] => (users ?? []).filter(user => userId == null || user.id !== String(userId));

/**
 * The reviewer a proposal was sent to, as an option of the reviewer select: labelled like the
 * regular options (only the username when Darwin no longer has that user).
 */
export const toRequestedReviewerOption = (
  reviewers: AssignmentProposalReviewer[] | null | undefined,
): ReviewerOption | null => {
  const reviewer = reviewers?.[0];

  return reviewer ? { label: reviewerLabel(reviewer), value: reviewer.username } : null;
};

/**
 * What the user does with a proposal in the editor. Both sent steps and any state the front does
 * not know are read-only: that is the safe default.
 */
export const toActiveProposalMode = (
  status: AssignmentProposalStatus | null | undefined,
): ActiveProposalMode => {
  if (status === AssignmentProposalStatus.Draft) return "draft";
  if (status === AssignmentProposalStatus.Rejected) return "rejected";

  return "sent";
};

interface OpenedProposalParams {
  linkId: string;
  proposal: AssignmentProposal;
  rejectionReason: string | null;
  isReview: boolean;
  /** Review only: the editor's changesets right after the proposal was applied (fingerprint). */
  loadedChangesets: string | null;
}

/**
 * The proposal the editor binds to once it opened it from its URL. A review also keeps what the
 * reviewer may do, the step it waits at, who is named in its notices and the applied changesets.
 */
export const toActiveProposal = ({
  linkId,
  proposal,
  rejectionReason,
  isReview,
  loadedChangesets,
}: OpenedProposalParams): ActiveProposal => {
  const opened: ActiveProposal = {
    linkId,
    proposalId: proposal.id,
    mode: isReview ? "review" : toActiveProposalMode(proposal.status),
    origin: "drawer",
    rejectionReason,
    reviewer: toRequestedReviewerOption(proposal.requestedReviewers),
  };

  if (!isReview) return opened;

  const intermediateApprover = proposal.requestedReviewers?.[0];

  return {
    ...opened,
    allowedActions: proposal.allowedActions ?? [],
    status: proposal.status ?? null,
    requesterName: proposal.createdBy ? userDisplayName(proposal.createdBy) : null,
    intermediateApproverName: intermediateApprover ? userDisplayName(intermediateApprover) : null,
    loadedChangesets,
  };
};
